import { ProfilePictureWooDark } from '@/components/profile-picture/profile-picture.woo-dark';
import { SkeletonWooDark } from '@/components/skeleton/skeleton.woo-dark';
import { useGetPersonalInfo } from '@/hooks/api';
import { normalizedFirstName } from '@/lib';
import { ArrowLeft } from '@nosinovacao/fe-icons';
import { Button, cn, Sheet, useI18n, useInMediaQuery } from '@nosinovacao/nosid-mfe-common';
import { createContext, type ReactNode, useContext, useState } from 'react';
import { type SettingsStep, type StepConfig } from './models';
import { SettingsMainSectionWooDark } from './main-section';
import { SettingsLanguageSectionWooDark } from './language-section';

interface NavigationState {
  step: SettingsStep;
  params?: Record<string, unknown>;
}

const UserSettingsWooDarkContext = createContext({ open: false, setOpen: (_: boolean) => {} });

const stepsConfiguration: Record<Exclude<SettingsStep, 'delete-account'>, StepConfig> = {
  main: {
    component: SettingsMainSectionWooDark,
    withBack: false,
  },
  language: {
    component: SettingsLanguageSectionWooDark,
    title: 'Common.Language.Title',
    withBack: true,
  },
};

export function UserSettingsSheetWooDark({ children }: Readonly<{ children?: ReactNode }>) {
  const [open, setOpen] = useState(false);
  const value = { open, setOpen };
  return (
    <UserSettingsWooDarkContext.Provider value={value}>
      {children}
      <UserSettingsContentWooDark />
    </UserSettingsWooDarkContext.Provider>
  );
}

export function UserSettingsContentWooDark() {
  const { open, setOpen } = useContext(UserSettingsWooDarkContext);
  const [navigationStack, setNavigationStack] = useState<NavigationState[]>([{ step: 'main' }]);
  const { t } = useI18n();

  const currentNav = navigationStack.at(-1)!;
  const {
    component: StepComponent,
    title,
    subtitle,
    withBack,
  } = stepsConfiguration[currentNav.step as Exclude<SettingsStep, 'delete-account'>];

  const resolvedTitle =
    (currentNav.params?.title as string | undefined) ?? (title ? t(title) : undefined);

  const resolvedSubtitle =
    (currentNav.params?.subtitle as string | undefined) ?? (subtitle ? t(subtitle) : undefined);

  const goToStep = (step: SettingsStep, params?: Record<string, unknown>) => {
    setNavigationStack((prev) => [...prev, { step, params }]);
  };

  const goBack = () => {
    setNavigationStack((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev));
  };
  const goToMain = () => {
    setNavigationStack([{ step: 'main' }]);
  };

  return (
    <Sheet
      skin="woo-dark"
      className="z-[60] tablet:pb-0"
      overlayClassName="z-[60]"
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          setNavigationStack([{ step: 'main' }]);
        }
        setOpen(o);
      }}
      title={resolvedTitle}
      subtitle={resolvedSubtitle}
      back={
        withBack ? (
          <Button skin="woo-dark" variant="tertiary" className="text-woo-blue" onClick={goBack}>
            <ArrowLeft width={20} height={20} className="text-nos-neutral-700" />
            {t('Common.Back')}
          </Button>
        ) : null
      }
    >
      <StepComponent goToMain={goToMain} goToStep={goToStep} params={currentNav.params} />
    </Sheet>
  );
}

export function UserSettingsTriggerWooDark() {
  const { isLoading, data } = useGetPersonalInfo();
  const isDesktop = useInMediaQuery('desktop-md');
  const { setOpen } = useContext(UserSettingsWooDarkContext);
  return (
    <SkeletonWooDark loading={isLoading} className="size-10 rounded-full">
      <button
        type="button"
        className={cn(
          'flex cursor-pointer items-center gap-2 rounded-lg transition-colors [&>*]:shrink-0',
          'outline-none desktop-md:px-[6px] desktop-md:py-1 desktop-md:-outline-offset-2',
          'desktop-md:hover:bg-nos-neutral-900 desktop-md:hover:outline-woo-green',
          'focus-visible:outline-woo-blue desktop-md:focus-visible:bg-nos-neutral-900 desktop-md:focus-visible:outline-woo-green',
        )}
        data-tid="button-user-settings"
        onClick={() => setOpen(true)}
      >
        <ProfilePictureWooDark size={40} name={data?.DisplayName} src={data?.PamPictureUri} />
        {data?.DisplayName && isDesktop ? (
          <span className="font-medium text-nos-neutral-0 font-text-p1">
            {normalizedFirstName(data?.DisplayName)}
          </span>
        ) : null}
      </button>
    </SkeletonWooDark>
  );
}
